import { ProjectSetupState, ProjectSetupPhase, ProjectWithSummary } from "@/types";
import { useQuery } from "@tanstack/react-query";

interface DocumentAnalysisResponse {
  hasDocuments: boolean;
  hasContract: boolean;
  contractExtracted: boolean;
  documentCount: number;
  contractDocument?: {
    fileName: string;
    uploadedAt: string;
    fileSize: string;
    versionNumber: number;
  } | null;
}

/**
 * Determines the current setup state of a project based on available data
 */
export function getProjectSetupState(
  projectData: ProjectWithSummary | undefined,
  hasDocuments: boolean = false,
  hasContract: boolean = false,
  contractExtracted: boolean = false
): ProjectSetupState {
  
  const documentsConfirmed = projectData?.project.documentsConfirmed || false;
  
  // Define the order of project setup phases
  const phaseOrder: ProjectSetupPhase[] = ['setup', 'documents', 'contract-extracted', 'active'];
  
  // Determine what phase we're actually in based on completion criteria
  let actualPhase: ProjectSetupPhase = 'setup';
  
  // Always start at setup phase since we're viewing the project
  if (hasDocuments && documentsConfirmed) {
    // Documents are uploaded and confirmed, move to contract extraction phase
    actualPhase = 'contract-extracted';
  }
  
  if (contractExtracted) {
    // Contract has been extracted, check if project should be active
    const hasContractData = projectData?.project.contractAmount && projectData.project.contractAmount > 0;
    if (hasContractData) {
      actualPhase = 'active';
    } else {
      // Contract extracted but no data yet - stay in contract-extracted phase
      actualPhase = 'contract-extracted';
    }
  }
  
  // If we don't have documents or they're not confirmed yet, we're still in documents phase
  if (!hasDocuments || !documentsConfirmed) {
    actualPhase = 'documents';
  }
  
  // Find current phase index
  const currentPhaseIndex = phaseOrder.indexOf(actualPhase);
  
  // Determine completed steps (all phases before current phase)
  const completedSteps: ProjectSetupPhase[] = phaseOrder.slice(0, currentPhaseIndex);
  
  // Current phase is the phase we're working on
  const currentPhase = actualPhase;
  
  // Determine next action
  let nextAction = '';
  if (!hasDocuments) {
    nextAction = 'Upload project documents, especially the contract';
  } else if (hasDocuments && !documentsConfirmed) {
    nextAction = 'Confirm that contract and compliance documents have been added to proceed';
  } else if (hasDocuments && documentsConfirmed && !hasContract) {
    nextAction = 'Upload the main contract document';
  } else if (hasContract && !contractExtracted) {
    nextAction = 'Extract key details from the contract';
  } else if (contractExtracted && !(projectData?.project.contractAmount && projectData.project.contractAmount > 0)) {
    nextAction = 'Review extracted contract data';
  } else {
    nextAction = 'Project setup complete - ready for claims management';
  }
  
  return {
    currentPhase,
    hasDocuments,
    hasContract,
    contractExtracted,
    nextAction,
    completedSteps
  };
}

/**
 * Hook to analyze project documents using the API
 */
export function useDocumentAnalysis(projectId: string) {
  return useQuery<DocumentAnalysisResponse>({
    queryKey: [`/api/projects/${projectId}/documents/analysis`],
    enabled: !!projectId && projectId !== 'new',
    staleTime: 30000, // Cache for 30 seconds
  });
}

/**
 * Mock function to simulate document analysis for backwards compatibility
 * @deprecated Use useDocumentAnalysis hook instead
 */
export function analyzeDocuments() {
  // This would typically make an API call to analyze uploaded documents
  // For now, we'll just simulate the logic
  return {
    hasDocuments: false, // Would check if any documents exist
    hasContract: false,  // Would check if a contract document exists
    contractExtracted: false // Would check if contract data has been extracted
  };
}

/**
 * Checks if a project setup state indicates the project is ready for active use
 */
export function isProjectReady(setupState: ProjectSetupState): boolean {
  return setupState.contractExtracted && setupState.hasContract;
}

/**
 * Gets the completion percentage for the project setup
 */
export function getSetupCompletionPercentage(setupState: ProjectSetupState): number {
  const totalSteps = 4; // setup, documents, contract-extracted, active
  const completedSteps = setupState.completedSteps.length;
  return Math.round((completedSteps / totalSteps) * 100);
} 